import store from "@plugins/store";
import userGoogleCredentialApi from "@apis/user.google.credential.api";
import { isEmpty } from "@utils/var.check";
import {
  CREDENTIAL_CHECK_MAX_WAIT,
  USER_CREDENTIAL_REQUEST_CONTEXT,
} from "@plugins/google/google.constant";
import { googleAuth } from "@plugins/google/google.auth";
import { UserCredentialRequestContext } from "@plugins/google/types.ts";

class UserGoogleCredential {
  userLinkedAccountEmail = ""; // email that user linked to access google
  accessToken = "";
  accessTokenExpiredDate = null;
  isAccessTokenExpired = true;
  lastCredentialCheck = null;

  getUserLinkedAccountEmail() {
    return this.userLinkedAccountEmail;
  }

  getAccessToken() {
    return this.accessToken;
  }

  getCredential() {
    return {
      userLinkedEmail: this.userLinkedAccountEmail,
      accessToken: this.accessToken,
      isAccessTokenExpired: this.isAccessTokenExpired,
    };
  }

  hasAccessToken() {
    return !isEmpty(this.accessToken);
  }

  fetchLinkedAccountCredential(
    context: UserCredentialRequestContext = USER_CREDENTIAL_REQUEST_CONTEXT.API_ACCESS,
  ) {
    return new Promise((resolve, reject) => {
      if (
        !isEmpty(this.lastCredentialCheck) &&
        Date.now() - this.lastCredentialCheck < CREDENTIAL_CHECK_MAX_WAIT
      ) {
        this.checkAccessTokenExpiredState();
        resolve();
        return;
      }

      const params = { context };
      userGoogleCredentialApi.fetchCredential(params).then(
        (response) => {
          if (response.code === "ok") {
            this.setCredential(response.data);
          }
          resolve();

          if (context !== USER_CREDENTIAL_REQUEST_CONTEXT.ACCOUNT_LINKING) {
            this.lastCredentialCheck = Date.now();
          }
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  // TODO: [Google] decouple this to lambda? this should be transparent to frontend, lambda always make sure the token is valid
  refreshLinkedAccountCredential() {
    return new Promise((resolve, reject) => {
      userGoogleCredentialApi.refreshCredential().then(
        (response) => {
          if (response.code !== "ok") {
            // In case where user revokes drive permission from their google account settings
            // and when attempting to refresh linked account credential, backend returns access denied error.
            // will attempt to ask user to re-sign in as well as grant the required drive permission
            googleAuth.checkAuthInstanceInitState().then(() => {
              googleAuth.handleRequestCode();
            });
            return reject(response.errorMap["0"]);
          }
          this.setCredential(response.data);
          this.lastCredentialCheck = Date.now();
          resolve();
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  linkAccount(authCode) {
    return new Promise((resolve, reject) => {
      userGoogleCredentialApi.linkAccount(authCode).then(
        (response) => {
          if (response.code === "ok") {
            this.setCredential(response.data);
          }
          resolve();
          this.lastCredentialCheck = Date.now();
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  unlinkAccount() {
    return new Promise((resolve, reject) => {
      userGoogleCredentialApi.unlinkAccount().then(
        (response) => {
          if (response.code === "ok") {
            this.userLinkedAccountEmail = "";
            this.accessToken = "";
            this.accessTokenExpiredDate = null;
            this.isAccessTokenExpired = true;
          }
          resolve();
          this.lastCredentialCheck = Date.now();
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  setCredential(data) {
    this.userLinkedAccountEmail = !isEmpty(data.email) ? data.email : "";
    this.accessToken = !isEmpty(data.accessToken) ? data.accessToken : "";
    this.accessTokenExpiredDate = data.expiredTime;
    this.checkAccessTokenExpiredState();
  }

  checkAccessTokenExpiredState() {
    if (isEmpty(this.accessTokenExpiredDate)) {
      this.isAccessTokenExpired = true;
    } else {
      const serverTime = store.getters["app/serverTime"];

      if (serverTime >= this.accessTokenExpiredDate) {
        this.isAccessTokenExpired = true;
      } else {
        this.isAccessTokenExpired = false;
      }
    }
  }
}

const userGoogleCredential = new UserGoogleCredential();

export { userGoogleCredential };
